import { _, hooks, mui, React, ts, ui, useSelector } from '_core';

import * as columns from 'views/preferences/columns-config';
import AsyncTabular from 'views/report/tables/async-tabular';

import { Params } from './types';
import { getColumns } from '../../common/utils/chart-utils';

type ChartProps = {
  id: string;
  fullScreen: boolean;
  snapshot: string;
  snapshotDate: string;
  goFullScreen: (_v: string) => void;
  loadWidgetData: (_payload: ts.types.widgets.WidgetGetDataParams) => Promise<ts.types.widgets.WidgetGetDataResponse>;
  params: Params;
  setParams: (_p: Params) => void;
  context: ts.types.riskModel.RiskModelAnalysisContext;
  queryParams?: { initial?: string };
};

const Chart = ({
  id,
  loadWidgetData,
  params,
  setParams,
  snapshot,
  fullScreen,
  goFullScreen,
  queryParams,
  snapshotDate,
  context,
}: ChartProps) => {
  const resources = useSelector((state) => state.resources);
  const container = React.createRef();

  const [tableParams, setTableParams] = React.useState<ts.types.components.dataGrid.TableParamsSheet>(
    params.table_params
  );

  const [tableDtypes, setTableDtypes] = React.useState<ts.types.components.dataGrid.TableDtypesSheet>(
    params.table_dtypes
  );

  const [show, setShow] = React.useState('show_all');
  const [filter, setFilter] = React.useState<ts.types.components.dataGrid.ValueFilters>([]);

  hooks.useEffectWithoutFirst(() => {
    setParams({
      ...params,
      table_params: { ...tableParams },
      table_dtypes: { ...tableDtypes },
    });
  }, [tableParams, tableDtypes]);

  const loadData = async (
    offset: number,
    orderBy: ts.types.components.dataGrid.OrderBy,
    filters: ts.types.components.dataGrid.ValueFilters,
    logicOperator: mui.dataGrid.GridLogicOperator
  ) => {
    const response = await loadWidgetData({
      data: {
        file: snapshot,
        pagination: { offset, limit: 100 },
        order_by: orderBy,
        values_filter: filters,
        filter_conjunction: logicOperator,
      },
    });

    return response;
  };

  const downloadData = async (rename: Record<string, string>, columnOrder: string[]) => {
    const response = await loadWidgetData({
      data: {
        file: snapshot,
        file_name: 'risk_model_universe_coverage_preview.csv',
        rename,
        column_order: columnOrder,
      },
      download: true,
    });
    return response;
  };

  const preferencesButton = () => (
    <mui.core.Box display="flex" width="100%">
      <mui.core.Box flexGrow={1}>
        <mui.core.Stack flexDirection="row">
          <ui.InfoRow title="Universe Coverage For">{snapshotDate}</ui.InfoRow>
          <mui.core.RadioGroup
            row
            name="members"
            value={show}
            onChange={(e) => {
              setShow(e.target.value as 'show_all' | 'in_rm');
            }}
            style={{ display: 'flex' }}
          >
            <mui.core.FormControlLabel
              value="show_all"
              control={<mui.core.Radio color="primary" />}
              label="Show All"
              labelPlacement="end"
            />
            <mui.core.FormControlLabel
              value="in_rm"
              control={<mui.core.Radio color="primary" />}
              label="Show Only in Risk Model"
              labelPlacement="end"
            />
          </mui.core.RadioGroup>
        </mui.core.Stack>
      </mui.core.Box>
    </mui.core.Box>
  );

  hooks.useEffectWithoutFirst(() => {
    if (show == 'in_rm')
      setFilter([
        {
          column: 'in_rm',
          operator: 'is',
          value: true,
          column_type: 'boolean',
        },
      ]);
    else setFilter([]);
  }, [show]);

  const renderChart = () => (
    <>
      <AsyncTabular<ts.enums.REPORT_ENUMS.TABLE_SHEET_KEY_ENUM.MAIN>
        key={snapshot}
        loadData={{
          [ts.enums.REPORT_ENUMS.TABLE_SHEET_KEY_ENUM.MAIN]: loadData,
        }}
        getColumns={{
          [ts.enums.REPORT_ENUMS.TABLE_SHEET_KEY_ENUM.MAIN]: (colKeys, userColumnsPreferences) =>
            getColumns(
              colKeys,
              userColumnsPreferences[ts.enums.REPORT_ENUMS.TABLE_SHEET_KEY_ENUM.MAIN],
              columns.riskModelPreviewColumns[ts.enums.REPORT_ENUMS.TABLE_SHEET_KEY_ENUM.MAIN],
              context,
              resources
            ),
        }}
        sheetTitles={{
          [ts.enums.REPORT_ENUMS.TABLE_SHEET_KEY_ENUM.MAIN]: 'Universe Coverage',
        }}
        sheetFilters={{
          [ts.enums.REPORT_ENUMS.TABLE_SHEET_KEY_ENUM.MAIN]: filter,
        }}
        fullScreen={fullScreen}
        goFullScreen={goFullScreen}
        queryParams={queryParams}
        customToolBar={{
          [ts.enums.REPORT_ENUMS.TABLE_SHEET_KEY_ENUM.MAIN]: preferencesButton,
        }}
        downloadFullData={{
          [ts.enums.REPORT_ENUMS.TABLE_SHEET_KEY_ENUM.MAIN]: downloadData,
        }}
        preferences={{
          preferencesSourceWidgetId: id,
          preferenceKey: ts.enums.PREFERENCES_KEY_ENUM.RISK_MODEL_SNAPSHOT_COLS,
          preferenceTab: ts.enums.PREFERENCES_INNER_TABS_ENUM.RISK_MODEL_PREVIEW_COLS,
        }}
        tableParams={tableParams}
        setTableParams={{
          [ts.enums.REPORT_ENUMS.TABLE_SHEET_KEY_ENUM.MAIN]: (v) =>
            setTableParams((t) => ({
              ...t,
              [ts.enums.REPORT_ENUMS.TABLE_SHEET_KEY_ENUM.MAIN]: v,
            })),
        }}
        tableDtypes={tableDtypes}
        setTableDtypes={{
          [ts.enums.REPORT_ENUMS.TABLE_SHEET_KEY_ENUM.MAIN]: (v) =>
            setTableDtypes((t) => ({
              ...t,
              [ts.enums.REPORT_ENUMS.TABLE_SHEET_KEY_ENUM.MAIN]: v,
            })),
        }}
      />
    </>
  );

  return (
    <mui.core.Box display="flex" flexDirection="column" style={{ height: '100%' }} ref={container}>
      {!_.isNil(snapshot) && renderChart()}
    </mui.core.Box>
  );
};

export default Chart;
